import React, { createContext, useState, useContext, useEffect } from 'react';

const AppThemeContext = createContext();

export const useAppTheme = () => {
  const context = useContext(AppThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within an AppThemeProvider');
  }
  return context;
};

export const AppThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    // Get saved theme from localStorage or default to 'default'
    const savedTheme = localStorage.getItem('dispatcher-theme');
    const validThemes = ['default', 'nightvision', 'submarine', 'bladerunner', 'orbit', 'cyberpunk', 'overwatch', 'corporate', 'blackbird', 'apollo']; // 'graveyard' VAULTED until Halloween 2026

    // If saved theme is invalid, reset to default
    if (savedTheme && !validThemes.includes(savedTheme)) {
      // localStorage can throw when full/private-mode; the theme still
      // works for the session either way
      try { localStorage.setItem('dispatcher-theme', 'default'); } catch {}
      return 'default';
    }

    return savedTheme || 'default';
  });

  useEffect(() => {
    // Save theme preference to localStorage. A throw here (storage full,
    // private mode) must not crash the tree — the theme still applies.
    try { localStorage.setItem('dispatcher-theme', currentTheme); } catch {}

    // Apply theme class to body
    // Remove all theme classes first
    document.body.classList.remove('theme-default', 'theme-nightvision', 'theme-submarine', 'theme-bladerunner', 'theme-orbit', 'theme-cyberpunk', 'theme-overwatch', 'theme-corporate', 'theme-blackbird', 'theme-icedout', 'theme-apollo', 'theme-graveyard'); // theme-icedout kept in sweep for stale sessions (theme deleted 2026-06-12)

    // Clean up dynamic overlays before applying new theme
    document
      .querySelectorAll(
        '.bubble-1, .bubble-2, .bubble-3, .bubble-4, .bubble-5, .bubble-6, .bubble-7, .bubble-8, .particle-field, .pressure-wave, .scanner-line, .br-rain-layer, .orbital-starfield, .orbital-satellite, .cyber-scanner, .cyber-scanlines, .cyber-vignette, .cyber-noise, .cyber-neon-glow, .cyber-holo-overlay, .ow-banner, .ow-radar, .ow-sweep, .ow-track, .ow-sat, .bb-worldmap, .bb-sweep, .bb-starchart, .bb-take, .bb-blip-1, .bb-blip-2, .bb-blip-3, .bb-blip-4, .bb-blip-5, .bb-blip-6, .ice-sparkle-1, .ice-sparkle-2, .ice-sparkle-3, .ice-sparkle-4, .ice-sparkle-5, .ice-sparkle-6, .ice-sparkle-7, .ice-sparkle-8, .ice-chain, .apollo-plot, .gy-fog-1, .gy-fog-2, .gy-fog-3, .gy-wisp-1, .gy-wisp-2, .gy-wisp-3, .gy-moon, .gy-scene, .gy-lightning'
      )
      .forEach(el => el.remove());

    // Add current theme class
    if (currentTheme === 'nightvision') {
      document.body.classList.add('theme-nightvision');
    } else if (currentTheme === 'submarine') {
      document.body.classList.add('theme-submarine');
      // Create bubble elements for submarine theme
      if (!document.querySelector('.bubble-1')) {
        const bubbles = ['bubble-1', 'bubble-2', 'bubble-3', 'bubble-4', 'bubble-5', 'bubble-6', 'bubble-7', 'bubble-8'];
        bubbles.forEach(className => {
          const bubble = document.createElement('div');
          bubble.className = className;
          document.body.appendChild(bubble);
        });
      }
      // Create pressure wave element
      if (!document.querySelector('.pressure-wave')) {
        const pressureWave = document.createElement('div');
        pressureWave.className = 'pressure-wave';
        document.body.appendChild(pressureWave);
      }
      // Scanner sweep line removed - not desired
    } else if (currentTheme === 'bladerunner') {
      document.body.classList.add('theme-bladerunner');
      if (!document.querySelector('.br-rain-layer')) {
        const rainLayer = document.createElement('div');
        rainLayer.className = 'br-rain-layer';
        document.body.appendChild(rainLayer);
      }
    } else if (currentTheme === 'orbit') {
      document.body.classList.add('theme-orbit');
      if (!document.querySelector('.orbital-starfield')) {
        const starfield = document.createElement('div');
        starfield.className = 'orbital-starfield';
        document.body.appendChild(starfield);
      }
      if (!document.querySelector('.orbital-satellite')) {
        const satellite = document.createElement('div');
        satellite.className = 'orbital-satellite';
        document.body.appendChild(satellite);
      }
    } else if (currentTheme === 'cyberpunk') {
      document.body.classList.add('theme-cyberpunk');
      // Create scanner sweep like Dune theme
      if (!document.querySelector('.cyber-scanner')) {
        const scanner = document.createElement('div');
        scanner.className = 'cyber-scanner';
        document.body.appendChild(scanner);
      }
      // Rain removed - wasn't visible enough
      // Create subtle scanlines
      if (!document.querySelector('.cyber-scanlines')) {
        const scanlines = document.createElement('div');
        scanlines.className = 'cyber-scanlines';
        document.body.appendChild(scanlines);
      }
      // Create vignette effect
      if (!document.querySelector('.cyber-vignette')) {
        const vignette = document.createElement('div');
        vignette.className = 'cyber-vignette';
        document.body.appendChild(vignette);
      }
      // Create digital noise
      if (!document.querySelector('.cyber-noise')) {
        const noise = document.createElement('div');
        noise.className = 'cyber-noise';
        document.body.appendChild(noise);
      }
      // Create neon glow overlay
      if (!document.querySelector('.cyber-neon-glow')) {
        const neonGlow = document.createElement('div');
        neonGlow.className = 'cyber-neon-glow';
        document.body.appendChild(neonGlow);
      }
      // Grid removed - too distracting
      // Create holographic overlay
      if (!document.querySelector('.cyber-holo-overlay')) {
        const holo = document.createElement('div');
        holo.className = 'cyber-holo-overlay';
        document.body.appendChild(holo);
      }
    } else if (currentTheme === 'overwatch') {
      document.body.classList.add('theme-overwatch');
      // Ambiance: drifting imagery grid (pure CSS) + two surveillance
      // track reticles on independent patrols. (Banner/radar/sweep/satellite
      // retired — their selectors stay in the removal sweep above.)
      if (!document.querySelector('.ow-track:not(.ow-track--b)')) {
        const track = document.createElement('div');
        track.className = 'ow-track';
        document.body.appendChild(track);
      }
      if (!document.querySelector('.ow-track--b')) {
        const trackB = document.createElement('div');
        trackB.className = 'ow-track ow-track--b';
        document.body.appendChild(trackB);
      }
    } else if (currentTheme === 'corporate') {
      document.body.classList.add('theme-corporate');
      // No overlays — enterprise clean means exactly that
    } else if (currentTheme === 'blackbird') {
      document.body.classList.add('theme-blackbird');
      // The back seat: the ANS polar star chart, turning with the world,
      // fix reticle riding its catalog stars. (Polar board, radar sweep,
      // film-strip take, and blips all retired — their selectors stay in
      // the removal sweep above.)
      if (!document.querySelector('.bb-starchart')) {
        const chart = document.createElement('div');
        chart.className = 'bb-starchart';
        // Star names ride the rotating chart as live children so each can
        // counter-rotate and stay level (SVG text would tilt with the sky)
        [['polaris', 'POLARIS'], ['vega', 'VEGA'], ['antares', 'ANTARES'],
         ['canopus', 'CANOPUS'], ['rigel', 'RIGEL']].forEach(([key, name]) => {
          const label = document.createElement('span');
          label.className = `bb-star-label bb-star-label--${key}`;
          label.textContent = name;
          chart.appendChild(label);
        });
        document.body.appendChild(chart);
      }
    } else if (currentTheme === 'apollo') {
      document.body.classList.add('theme-apollo');
      // The plotboard: orbit ground-tracks with a spacecraft in transit
      if (!document.querySelector('.apollo-plot')) {
        const plot = document.createElement('div');
        plot.className = 'apollo-plot';
        document.body.appendChild(plot);
      }
    } else if (currentTheme === 'graveyard') {
      document.body.classList.add('theme-graveyard');
      // Ground fog (three banks) + will-o'-wisps
      ['gy-moon', 'gy-lightning', 'gy-scene', 'gy-fog-1', 'gy-fog-2', 'gy-fog-3', 'gy-wisp-1', 'gy-wisp-2', 'gy-wisp-3'].forEach((cls) => {
        if (!document.querySelector(`.${cls}`)) {
          const el = document.createElement('div');
          el.className = cls;
          document.body.appendChild(el);
        }
      });
    }
    // (icedout theme deleted 2026-06-12 — structural overrides broke navbar
    // stickiness and hid board content; its classes stay in the removal
    // sweep above so stale sessions get cleaned.)
  }, [currentTheme]);

  const toggleTheme = () => {
    setCurrentTheme(prev => {
      const themes = ['default', 'nightvision', 'submarine', 'bladerunner', 'orbit', 'cyberpunk', 'overwatch', 'corporate', 'blackbird', 'apollo']; // 'graveyard' vaulted
      const currentIndex = themes.indexOf(prev);
      const nextIndex = (currentIndex + 1) % themes.length;
      return themes[nextIndex];
    });
  };

  const setTheme = (theme) => {
    if (['default', 'nightvision', 'submarine', 'bladerunner', 'orbit', 'cyberpunk', 'overwatch', 'corporate', 'blackbird', 'apollo'].includes(theme)) { // 'graveyard' vaulted
      setCurrentTheme(theme);
    }
  };

  return (
    <AppThemeContext.Provider value={{ currentTheme, toggleTheme, setTheme }}>
      {children}
    </AppThemeContext.Provider>
  );
};
